import { useState, useEffect } from "react";
import { useDashboard } from "store/dashboard.store";
import DataTable from "components/layout-components/Datatable";
import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.css";
import "gridstack/dist/gridstack-extra.min.css";
import { Row, Col, Button, Input } from "antd";
import IntegrationService from "services/IntegrationService";
import { USER_ID } from "constants/AuthConstant";

let grid;

const sizes = {
    P: { width: "30%", height: "300px" },
    M: { width: "60%", height: "450px" },
    G: { width: "100%", height: "600px" },
};

const formatUrl = (url) => {
    if (!url) return "";
    url = url.trim();
    if (url === "www." || url.length < 5) {
        return "";
    }
    const urlPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+)(.*)$/;
    const match = url.match(urlPattern);
    if (!match) {
        return "";
    }
    if (url.startsWith('http://www.')) {
        return url.replace('http://', 'https://');
    } else if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    } else if (url.startsWith('www.')) {
        return `https://${url}`;
    } else if (!url.startsWith('https://')) {
        return `https://${url}`;
    }
    return url;
};

const isValidUrl = (url) => {
    try {
        new URL(formatUrl(url));
        return true;
    } catch (_) {
        return false;
    }
};

const convertToEmbedUrl = (url) => {
    try {
        const urlObj = new URL(url);
        if (urlObj.hostname.includes('google.com.br') || urlObj.hostname.includes('google.com')) {
            // Extrai as coordenadas da URL
            const match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+),(\d+z)/);
            if (match) {
                const [, lat, lng, zoom] = match;
                return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d100000!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${lat}%2C${lng}!5e0!3m2!1spt-BR!2sbr!4v1`;
            }
        }
        return url;
    } catch (error) {
        console.error('Erro ao converter URL:', error);
        return url;
    }
};

export const DefaultDashboard = () => {
    const { setWidgetPosition, widgets } = useDashboard();
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [link, setLink] = useState("");
    const [size, setSize] = useState("G");
    const [tableData, setTableData] = useState([]);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        grid = GridStack.init({ column: 3 });
        grid.cellHeight(280);
        grid.resizable(".grid-stack-item", true);
        grid.on("dragstop", (event, el) => {
            const { id, x, y, h, w } = el.gridstackNode;
            setWidgetPosition(id, { x, y }, { w, h });
        });
    }, []);

    useEffect(() => {
        if (grid) {
            widgets.forEach((widget) => {
                grid.update(`${widget.widget}`, widget.position);
            });
        }
    }, [widgets]);

    const carregarIntegracoes = async () => {
        setLoading(true);
        try {
            const response = await IntegrationService.getIntegration();
            if (response.status === 200) {
                setTableData(response.data);
            }
        } catch (error) {
            console.error("Erro ao carregar integrações:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarIntegracoes();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir este item?")) return;

        try {
            await IntegrationService.deleteIntegration(id);
            alert("Item excluído com sucesso!");
            carregarIntegracoes();
        } catch (error) {
            console.error("Erro ao excluir item:", error);
            alert("Erro ao excluir o item.");
        }
    };

    const handleEdit = ({ id, nome, link, tamanho }) => {
        setEditingId(id);
        setName(nome);
        setLink(link);
        setSize(tamanho);
    };

    const handleCancel = () => {
        setEditingId(null);
        setName("");
        setLink("");
        setSize("G");
    };

    const handleToggleAtivo = async (id, novoValor) => {
        const item = tableData.find((i) => i.id === id);
        if (!item) return;
    
        const updatedItem = { ...item, ativo: novoValor };
    
        try {
            await IntegrationService.updateIntegration(id, updatedItem);
            carregarIntegracoes();
        } catch (error) {
            console.error("Erro ao atualizar ativo:", error);
            alert("Erro ao atualizar o status de exibição.");
        }
    };

    const handleSave = async () => {
        if (!name || !link || !size) {
            alert("Preencha todos os campos antes de salvar.");
            return;
        }
        const formattedLink = formatUrl(link);
        if (!formattedLink) {
            alert("Por favor, insira um link válido e completo.");
            return;
        }
        setLoading(true);
        try {
            const newData = {
                id: editingId || 0,
                iD_USUARIO: localStorage.getItem(USER_ID),
                nome: name,
                link: formattedLink,
                tamanho: size,
                ativo: true,
            };
            const response = editingId
                ? await IntegrationService.updateIntegration(editingId, newData)
                : await IntegrationService.createIntegration(newData);
            if ([200, 201].includes(response.status)) {
                alert("Dados salvos com sucesso!");
                carregarIntegracoes();
                handleCancel();
            } else {
                alert("Falha ao salvar os dados.");
            }
        } catch (error) {
            console.error("Erro:", error);
            alert("Ocorreu um erro ao salvar os dados.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div
                className={loading ? "opacity-0-3" : undefined}
                style={{ pointerEvents: loading ? "none" : "auto", flex: 1 }}
            >
                <Row gutter={8}>
                    <Col xs={24} className="mb-0">
                        <h5 className="mb-1 font-weight-bolder font-size-md" style={{ fontFamily: "PoppinsMedium" }}>
                            Cadastro de novos dados (integração)
                        </h5>
                        <div style={{ marginBottom: '2rem' }} />

                        <div className="grid-stack">
                            <Row gutter={8} style={{ display: 'flex' }}>
                                <Col xs={24} md={8} style={{ flex: 1 }}>
                                    <label htmlFor="name" className="form-label">Nome:</label>
                                    <div className="mb-2" />
                                    <Input id="name" placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
                                </Col>

                                <Col xs={24} md={8} style={{ flex: 1 }}>
                                    <label htmlFor="link" className="form-label">Link:</label>
                                    <div className="mb-2" />
                                    <Input id="link" placeholder="Link" value={link} onChange={(e) => setLink(e.target.value)} style={{ width: '100%' }} />
                                </Col>

                                <Col xs={24} md={8} style={{ flex: 1 }}>
                                    <label className="form-label">Tamanho:</label>
                                    <div className="mb-2" />
                                    <div>
                                        {["P", "M", "G"].map((t) => (
                                            <Button
                                                key={t}
                                                className={`btn btn-sm ${size === t ? "btn-primary" : "btn-outline-secondary"} me-1`}
                                                onClick={() => setSize(t)}
                                            >
                                                {t}
                                            </Button>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                            <div style={{ marginBottom: '3rem' }} />

                            {link && isValidUrl(link) ? (
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <iframe
                                        src={convertToEmbedUrl(formatUrl(link))}
                                        title={`Mapa da integração - ${name || "visualização"}`}
                                        style={{ ...sizes[size], borderRadius: '8px', border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                            ) : link ? (
                                <div style={{ padding: 10, color: "red", fontWeight: "bold" }}>
                                    Link inválido. Por favor, insira uma URL válida.
                                </div>
                            ) : null}

                            <div className="d-flex justify-content-center mt-5" style={{ marginBottom: '20px', gap: '10px' }}>
                                <div style={{ width: '120px' }}>
                                    <Button className="btn btn-secondary w-100" onClick={handleCancel}>
                                        Cancelar
                                    </Button>
                                </div>
                                <div style={{ width: '120px' }}>
                                    <Button className="btn btn-primary w-100" onClick={handleSave} loading={loading}>
                                        Salvar
                                    </Button>
                                </div>
                            </div>


                        </div>
                    </Col>
                </Row>
            </div>

            <DataTable
                data={tableData}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleAtivo={handleToggleAtivo}
                loading={loading}
            />
        </div>
    );
};

export default DefaultDashboard;
